import React from "react";

// Props interface with optional variants
interface DownloadButtonProps {
  variant?: "primary" | "secondary" | "outline";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
  variant = "primary",
  size = "md",
  disabled = false,
  className = "",
  children = "Download Sample CSV",
}) => {
  // Sample CSV data - you can customize this as needed
  const sampleCSVData = `name,email,phoneNumber,jobTitle
John Doe,john.doe@example.com,08000000000,Engineering
Jane Smith,jane.smith@example.com,08000000000,Marketing`;

  const handleDownload = () => {
    // Create a Blob with the CSV data
    const blob = new Blob([sampleCSVData], { type: "text/csv;charset=utf-8;" });

    // Create a temporary URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample-data.csv");

    // Append to body, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  // Base styles
  const baseStyles =
    "font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  // Variant styles
  const variantStyles = {
    primary: "bg-[#243677] hover:bg-blue-700 text-white focus:ring-blue-500",
    secondary: "bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500",
    outline:
      "border border-gray-300 hover:bg-gray-50 text-gray-700 focus:ring-blue-500",
  };

  // Size styles
  const sizeStyles = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  const buttonClasses = `
    ${baseStyles}
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${className}
  `.trim();

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={disabled}
      className={buttonClasses}
    >
      {children}
    </button>
  );
};

export default DownloadButton;
